import { useLayoutEffect, useRef } from 'react';

type ViewTransitionLike = {
  finished?: Promise<unknown>;
  updateCallbackDone?: Promise<unknown>;
  ready?: Promise<unknown>;
};

type ViewTransitionCapableDocument = Document & {
  startViewTransition?: (callback: () => void | Promise<void>) => ViewTransitionLike | undefined;
};

/**
 * Trigger a browser View Transition whenever `key` changes.
 *
 * Call this alongside a React state change that swaps out part of the tree
 * (e.g. route content). The paired element must carry a matching CSS
 * `view-transition-name` for the cross-fade to animate.
 *
 * Gracefully no-ops on browsers without `document.startViewTransition`.
 */
export function useViewTransition(key: string): void {
  const previousKeyRef = useRef<string>(key);

  useLayoutEffect(() => {
    if (previousKeyRef.current === key) return;
    previousKeyRef.current = key;
    const doc = document as ViewTransitionCapableDocument;
    if (typeof doc.startViewTransition !== 'function') return;
    try {
      const transition = doc.startViewTransition(() => {});
      // The ViewTransition's promises reject with InvalidStateError when the
      // transition is aborted (e.g. the document is torn down mid-transition,
      // as happens when a Storybook test unmounts). Attach no-op catch
      // handlers so these rejections don't surface as unhandled rejections.
      if (transition) {
        transition.finished?.catch(() => {});
        transition.updateCallbackDone?.catch(() => {});
        transition.ready?.catch(() => {});
      }
    } catch {
      // Older / partial implementations — silently skip.
    }
  }, [key]);
}
